/** Small pieces shared by breeding screens: status chips, due lines, calf chips. */
import { forwardRef, useId, type InputHTMLAttributes, type ReactNode } from "react";
import { Link } from "react-router";
import { daysBetween, localDate, type AnimalDetail, type Tone } from "../../../contract";
import { formatDate } from "../../../web/lib/format";
import { plural, t } from "../../../web/lib/i18n";
import { useMe } from "../../../web/lib/session";
import { Chip, Field, Icon, TagLabel, TextInput } from "../../../web/ui/kit";
import { CALF_SEX, EASE, SHORT_STATUS } from "../shared/labels";
import type { BreedingState, BreedingStatus, DueBasis } from "../shared/types";

export { SHORT_STATUS };
export const STATUS_TONE: Record<BreedingStatus, Tone> = {
  pregnant: "repro", bred: "repro", exposed: "info", open: "warn", calved: "good", unknown: "neutral",
};

export function StatusChip({ status, recheck }: { status: BreedingStatus; recheck?: boolean }) {
  return (
    <span className="breeding-chips">
      <Chip tone={STATUS_TONE[status]} icon={status === "pregnant" ? "calf" : status === "exposed" ? "fence" : "heart"}>{t(SHORT_STATUS[status])}</Chip>
      {recheck && <Chip tone="warn" icon="vet">{t("Recheck")}</Chip>}
    </span>
  );
}

/** "In 5 days", "Today", "3 days late". */
export function dueRelative(due: string, today: string): string {
  const days = daysBetween(today, due);
  if (days === 0) return t("Due today");
  if (days > 0) return plural(days, "In {{count}} day", "In {{count}} days");
  return plural(-days, "{{count}} day late", "{{count}} days late");
}

export function dueText(due: string, end?: string): string {
  return end ? t("Due to calve {{from}} to {{to}}", { from: formatDate(due), to: formatDate(end) }) : t("Due to calve {{date}}", { date: formatDate(due) });
}

/** How a due date was worked out, in words. */
export function basisText(s: Pick<BreedingState, "due_basis" | "due_assumed_embryo_age" | "gestation_days" | "conceived_on" | "due_end">): string {
  const basis = s.due_basis as DueBasis | undefined;
  const g = s.gestation_days;
  switch (basis) {
    case "fetal_age": return t("Calculated from the fetal age at the check and a {{days}}-day gestation.", { days: g });
    case "service": return s.due_end ? t("Calculated from the breeding dates and a {{days}}-day gestation.", { days: g }) : t("Calculated from the breeding on {{date}} and a {{days}}-day gestation.", { date: formatDate(s.conceived_on), days: g });
    case "transfer": return s.due_assumed_embryo_age
      ? t("Calculated from the transfer date and a {{days}}-day gestation, assuming a 7-day embryo.", { days: g })
      : t("Calculated from the transfer date, the embryo's age and a {{days}}-day gestation.", { days: g });
    case "exposure": return t("Calculated from when she was with the bulls and a {{days}}-day gestation. She could calve any time in this window.", { days: g });
    default: return t("No breeding date or fetal age is recorded, so there's no due date yet.");
  }
}

export function CalfChips({ calves }: { calves: Array<{ id?: string; label?: string; tag?: string; sex?: string; outcome?: string }> }) {
  if (!calves.length) return <span className="muted">{t("No calf details")}</span>;
  return (
    <span className="breeding-calves">
      {calves.map((c, i) => {
        const sex = c.sex === "female" || c.sex === "male" ? t(CALF_SEX[c.sex]) : t("Calf");
        const body = <>{c.tag ? <TagLabel tag={c.tag} size="sm" /> : c.label ? <span>{c.label}</span> : null}<span>{sex}</span>{c.outcome === "born_dead" && <Chip tone="bad">{t("Born dead")}</Chip>}</>;
        return c.id ? <Link key={i} to={`/cattle/${c.id}`} className="breeding-calf-chip">{body}</Link> : <span key={i} className="breeding-calf-chip">{body}</span>;
      })}
    </span>
  );
}

export function EaseChip({ ease }: { ease?: string }) {
  if (!ease) return null;
  return <Chip tone={ease === "no_help" ? "good" : ease === "easy_pull" ? "info" : "warn"} icon={ease === "no_help" ? "check" : "warn"}>{t(EASE[ease as keyof typeof EASE] ?? ease)}</Chip>;
}

export function DueBadge({ due, end, today }: { due: string; end?: string; today: string }) {
  const days = daysBetween(today, due);
  return (
    <span className={days < 0 ? "breeding-due is-late" : days <= 7 ? "breeding-due is-soon" : "breeding-due"}>
      <Icon name="calf" size={14} />{dueText(due, end)} · {dueRelative(due, today)}
    </span>
  );
}

// ---------------------------------------------------------------- who can do what

const breeding = (a: AnimalDetail) => a.facets.breeding as BreedingState | undefined;

export function isBull(a: AnimalDetail): boolean {
  return a.sex === "male" && !a.castrated && a.class === "bull";
}

/** Old enough to be bred, checked or calve (or already has breeding records). */
export function isBreedingFemale(a: AnimalDetail): boolean {
  if (a.sex === "male") return false;
  if (breeding(a)?.status && breeding(a)!.status !== "unknown") return true;
  return a.class === "cow" || a.class === "heifer" || (a.age_days ?? 400) >= 300;
}

export function bullIn(a: AnimalDetail): boolean {
  return !!breeding(a)?.bull?.in_with;
}

/** The ranch's calendar date. */
export function useToday(): string {
  const { data } = useMe();
  return data?.ranch ? localDate(new Date(), data.ranch.timezone) : new Date().toISOString().slice(0, 10);
}

/** A text input with its label tied to it, so screen readers and taps on the label find the box. */
export const TextField = forwardRef<HTMLInputElement, { label: ReactNode; hint?: ReactNode; error?: ReactNode; optional?: boolean } & InputHTMLAttributes<HTMLInputElement>>(
  function TextField({ label, hint, error, optional, ...input }, ref) {
    const id = useId();
    return <Field label={label} hint={hint} error={error} optional={optional} htmlFor={id}><TextInput ref={ref} id={id} {...input} /></Field>;
  },
);
